import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { buildApiUrl } from '../utils/apiConfig';
import { getConfig } from '../utils/config';
import polarisLogo from '../images/Polaris Logo.svg';
import '../styles/subscribe-details-page.css';

/**
 * Pre-checkout details page — collects email before Stripe payment.
 *
 * Two paths:
 *   1. Email only → /checkout directly (standard flow)
 *   2. Email + Clever (via OAuth button) → /subscribe/clever-confirm to verify school details
 *
 * The "Connect with Clever" button initiates the subscribe-callback OAuth flow.
 * Clever redirects back here with cleverId, cleverDistrictId, cleverSchoolId, cleverSchoolName
 * in URL query params (plus a state param to restore plan selection).
 *
 * Plan details (priceId, billingInterval, planName) come from:
 *   - location.state  (normal navigation from plan selection)
 *   - URL ?state=     (restored after Clever OAuth redirect, encoded as JSON)
 */

interface PlanState {
  priceId: string;
  billingInterval: 'month' | 'year';
  planName?: string;
}

interface CleverData {
  cleverId: string;
  cleverDistrictId: string;
  cleverDistrictName: string | null;
  cleverSchoolId: string | null;
  cleverSchoolName: string | null;
}

function isValidEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
}

export const SubscribeDetailsPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);

  // ── Plan details ────────────────────────────────────────────────────────────
  // Prefer navigation state; fall back to JSON encoded in the OAuth ?state= param
  const navState = location.state as PlanState | undefined;
  const stateParam = searchParams.get('state');
  const restoredState: PlanState | null = (() => {
    if (stateParam) {
      try { return JSON.parse(atob(stateParam)); } catch { return null; }
    }
    return null;
  })();

  const planState: PlanState | null = navState?.priceId
    ? navState
    : restoredState;

  const { priceId, billingInterval, planName } = planState || {};

  // ── Clever identity (present after OAuth redirect back) ──────────────────────
  const cleverIdParam        = searchParams.get('cleverId');
  const cleverDistrictId     = searchParams.get('cleverDistrictId') || '';
  const cleverDistrictName   = searchParams.get('cleverDistrictName');
  const cleverSchoolId       = searchParams.get('cleverSchoolId');
  const cleverSchoolName     = searchParams.get('cleverSchoolName');
  const cleverEmailParam     = searchParams.get('cleverEmail');
  const cleverData: CleverData | null = cleverIdParam
    ? { cleverId: cleverIdParam, cleverDistrictId, cleverDistrictName, cleverSchoolId, cleverSchoolName }
    : null;

  // Error from backend when Clever email is already registered (redirect with no clever params)
  const errorParam = searchParams.get('error');

  // ── Form state ───────────────────────────────────────────────────────────────
  const [email, setEmail] = useState(() => {
    if (cleverEmailParam && cleverEmailParam.trim()) return cleverEmailParam.trim();
    return '';
  });
  const [emailError, setEmailError]               = useState('');
  const [generalError, setGeneralError]           = useState('');
  const [isSubmitting, setIsSubmitting]           = useState(false);
  const [emailCheckState, setEmailCheckState]     = useState<'idle' | 'checking' | 'available' | 'taken'>('idle');

  // Redirect to signup if we have no plan at all
  useEffect(() => {
    if (!priceId) {
      navigate('/signup', { replace: true });
    }
  }, [priceId, navigate]);

  // When Clever provides an email, check availability on load so we can block submit if already taken
  const emailFromClever = !!(cleverData && cleverEmailParam && cleverEmailParam.trim());

  // Debounced email availability check
  useEffect(() => {
    if (!email || !isValidEmail(email)) {
      setEmailCheckState('idle');
      return;
    }
    setEmailCheckState('checking');
    const timer = setTimeout(async () => {
      try {
        const res = await fetch(buildApiUrl(`api/subscriptions/check-email?email=${encodeURIComponent(email.trim())}`));
        if (res.ok) {
          const data = await res.json();
          setEmailCheckState(data.available ? 'available' : 'taken');
          setEmailError(data.available ? '' : 'This email address is already registered. Please sign in instead.');
        }
      } catch {
        setEmailCheckState('idle');
      }
    }, 600);
    return () => clearTimeout(timer);
  }, [email]);

  // ── Clever OAuth ─────────────────────────────────────────────────────────────
  const handleConnectWithClever = () => {
    const { cleverClientId, cleverSubscribeRedirectUri } = getConfig();
    // Encode current plan state in the OAuth state param so we can restore it on return
    const encoded = btoa(JSON.stringify({ priceId, billingInterval, planName }));
    const cleverUrl = 'https://clever.com/oauth/authorize?response_type=code'
      + `&client_id=${encodeURIComponent(cleverClientId)}`
      + `&redirect_uri=${encodeURIComponent(cleverSubscribeRedirectUri)}`
      + `&scope=read:user_id read:email`
      + `&state=${encodeURIComponent(encoded)}`;
    window.location.href = cleverUrl;
  };

  const handleDisconnectClever = () => {
    // Strip Clever params from URL and stay on the page with plan state in nav state
    navigate('/subscribe/details', {
      replace: true,
      state: { priceId, billingInterval, planName },
    });
  };

  // ── Submit ───────────────────────────────────────────────────────────────────
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setEmailError('');
    setGeneralError('');

    if (!email.trim()) { setEmailError('Email address is required.'); return; }
    if (!isValidEmail(email)) { setEmailError('Please enter a valid email address.'); return; }
    if (emailCheckState === 'taken') {
      setEmailError('This email address is already registered. Please sign in instead.');
      return;
    }

    setIsSubmitting(true);
    try {
      // Final server-side email check
      if (emailCheckState !== 'available') {
        const res = await fetch(buildApiUrl(`api/subscriptions/check-email?email=${encodeURIComponent(email.trim())}`));
        if (res.ok) {
          const data = await res.json();
          if (!data.available) {
            setEmailError('This email address is already registered. Please sign in instead.');
            setIsSubmitting(false);
            return;
          }
        }
      }

      if (cleverData) {
        // Clever connected → show confirmation screen
        navigate('/subscribe/clever-confirm', {
          state: {
            priceId,
            billingInterval,
            planName,
            customerEmail: email.trim(),
            clever: cleverData,
          },
        });
      } else {
        // Email only → straight to checkout
        navigate('/checkout', {
          state: { priceId, billingInterval, customerEmail: email.trim() },
        });
      }
    } catch {
      setGeneralError('Something went wrong. Please try again.');
      setIsSubmitting(false);
    }
  };

  const emailStatusIcon = () => {
    if (emailCheckState === 'checking') return <span className="email-status checking">Checking…</span>;
    if (emailCheckState === 'available') return <span className="email-status available">✓ Available</span>;
    return null;
  };

  if (!priceId) return null;

  return (
    <div className="subscribe-details-page">
      <div className="subscribe-details-container">
        <div className="subscribe-details-logo">
          <img src={polarisLogo} alt="Polaris Athlete" />
        </div>

        <h1 className="subscribe-details-title">Complete your subscription</h1>
        {planName && <p className="subscribe-details-plan">Plan: <strong>{planName}</strong></p>}
        <p className="subscribe-details-subtitle">
          {emailFromClever
            ? 'Your Clever email will be used for your Polaris account.'
            : 'Enter your email address to create your Polaris account.'}
          {!cleverData && ' If your school uses Clever, you can connect your account below.'}
        </p>

        {errorParam === 'email_already_registered' && (
          <div className="sdf-general-error" role="alert">
            This email address is already registered. Please sign in instead.
          </div>
        )}

        <form className="subscribe-details-form" onSubmit={handleSubmit} noValidate>

          {/* Email field */}
          <div className={`sdf-field ${emailError ? 'sdf-field--error' : emailCheckState === 'available' ? 'sdf-field--ok' : ''}`}>
            <label htmlFor="sd-email" className="sdf-label">
              Email address <span className="sdf-required">*</span>
              {emailFromClever && (
                <span className="sdf-readonly-badge" title="From your Clever account">Clever</span>
              )}
            </label>
            <div className="sdf-input-row">
              <input
                id="sd-email"
                type="email"
                className="sdf-input"
                placeholder="you@school.edu"
                value={email}
                onChange={e => { setEmail(e.target.value); setEmailError(''); }}
                autoComplete="email"
                autoFocus={!emailFromClever}
                disabled={isSubmitting}
                readOnly={emailFromClever}
                aria-readonly={emailFromClever}
              />
              {emailStatusIcon()}
            </div>
            {emailError && <p className="sdf-error">{emailError}</p>}
            <p className="sdf-hint">This will be your Polaris username.</p>
          </div>

          {/* Clever section */}
          <div className="sdf-clever-section">
            {cleverData ? (
              /* Connected state */
              <div className="sdf-clever-connected">
                <div className="sdf-clever-connected-header">
                  <span className="sdf-clever-dot" />
                  <span className="sdf-clever-connected-label">Clever account connected</span>
                  <button
                    type="button"
                    className="sdf-clever-disconnect"
                    onClick={handleDisconnectClever}
                    title="Disconnect Clever"
                  >
                    ✕ Remove
                  </button>
                </div>
                <div className="sdf-clever-details">
                  {cleverData.cleverDistrictName && (
                    <div className="sdf-clever-detail-row">
                      <span className="sdf-clever-detail-label">District</span>
                      <span className="sdf-clever-detail-value">{cleverData.cleverDistrictName}</span>
                    </div>
                  )}
                  {cleverData.cleverSchoolName && (
                    <div className="sdf-clever-detail-row">
                      <span className="sdf-clever-detail-label">School (from Clever)</span>
                      <span className="sdf-clever-detail-value">{cleverData.cleverSchoolName}</span>
                    </div>
                  )}
                </div>
                <p className="sdf-hint">Your school details will be linked to your subscription automatically.</p>
              </div>
            ) : (
              /* Not connected state */
              <div className="sdf-clever-unconnected">
                <p className="sdf-clever-prompt">Does your school use Clever?</p>
                <button
                  type="button"
                  className="sdf-clever-btn"
                  onClick={handleConnectWithClever}
                  disabled={isSubmitting}
                >
                  <svg className="sdf-clever-icon" viewBox="0 0 32 32" width="20" height="20" fill="none">
                    <circle cx="16" cy="16" r="16" fill="#4274F6"/>
                    <path d="M22.5 12.5C21.3 10.4 19 9 16.3 9C12.3 9 9 12.1 9 16C9 19.9 12.3 23 16.3 23C19 23 21.3 21.6 22.5 19.5L19.5 17.8C18.9 18.8 17.7 19.5 16.3 19.5C14.2 19.5 12.5 17.9 12.5 16C12.5 14.1 14.2 12.5 16.3 12.5C17.7 12.5 18.9 13.2 19.5 14.2L22.5 12.5Z" fill="white"/>
                  </svg>
                  Connect with Clever
                </button>
                <p className="sdf-hint">Optional. Connecting Clever links your school automatically and lets you sign in with Clever later.</p>
              </div>
            )}
          </div>

          {generalError && <p className="sdf-general-error">{generalError}</p>}

          <div className="sdf-actions">
            <button
              type="submit"
              className="sdf-submit-btn"
              disabled={isSubmitting || emailCheckState === 'taken'}
            >
              {isSubmitting ? 'Please wait…' : 'Checkout'}
            </button>
            <button
              type="button"
              className="sdf-back-btn"
              onClick={() => navigate('/signup')}
            >
              Back to plans
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SubscribeDetailsPage;
